import { Tooltip } from "../utils/local-antd";
import React, { useState } from "react";
import styled from "styled-components";

import { truncateString } from "../utils/String";
import { ParagraphProps, Text } from "./Text";

const StyledText = styled(Text)`
  color: ${(props) =>
    props.color ? props.theme[props.color] : props.theme["textColor"]};
  white-space: nowrap;
`;

const EllipsisText = styled(StyledText)`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export type TextWithTruncatedProps = {
  children?: string;
  length?: number;
  toolTip?: boolean;
  /** Cuts the text with an ellipsis only as far as the space around it requires, instead of after `length` characters. The parent has to let it shrink (`min-width: 0`). */
  fitToWidth?: boolean;
} & ParagraphProps;

/** The tooltip is drawn outside the text, so it has to be told which way the text reads - a "+" leading a number in a right-to-left page would otherwise end up trailing it. */
const getTooltipTitle = (
  text: string | undefined,
  direction: ParagraphProps["direction"],
) =>
  direction === "ltr" || direction === "rtl" ? (
    <span dir={direction}>{text}</span>
  ) : (
    text
  );

export const TextWithTruncated: React.FC<TextWithTruncatedProps> = ({
  children,
  length = 40,
  toolTip = true,
  fitToWidth = false,
  ...props
}) => {
  const [overflowing, setOverflowing] = useState(false);

  if (fitToWidth) {
    return (
      <Tooltip
        title={
          toolTip && overflowing
            ? getTooltipTitle(children, props.direction)
            : undefined
        }
      >
        <EllipsisText
          {...props}
          onMouseEnter={(e) =>
            setOverflowing(
              e.currentTarget.scrollWidth > e.currentTarget.clientWidth,
            )
          }
        >
          {children}
        </EllipsisText>
      </Tooltip>
    );
  }

  if (children && children.length > length) {
    return (
      <Tooltip
        title={toolTip ? getTooltipTitle(children, props.direction) : undefined}
      >
        <StyledText {...props}>{truncateString(children, length)}</StyledText>
      </Tooltip>
    );
  }

  return <StyledText {...props}>{children}</StyledText>;
};
